import { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import { QueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Calendar, TrendingUp, DollarSign, Activity, AlertCircle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RecurringTransactionsList } from "@/components/recurring/RecurringTransactionsList";
import { RecurringFilters } from "@/components/recurring/RecurringFilters";
import { RecurringEditModal } from "@/components/recurring/RecurringEditModal";
import { MainLayout } from "@/components/MainLayout";
import { SEO } from "@/components/SEO";
import { RouteDisplay } from "@/components/navigation/RouteDisplay";
import { useRecurringTransactions } from "@/hooks/useRecurringTransactions";
import type { RecurringTransaction } from "@/hooks/useRecurringTransactions";
import { transformRecurringTransactions, calculateRecurringStatistics } from "@/utils/recurringDataTransformer";
import { toast } from "sonner";

const patternTypeLabels = {
  daily: "Diário",
  weekly: "Semanal",
  monthly: "Mensal",
  yearly: "Anual"
};

export default function RecurringPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [filters, setFilters] = useState({
    type: searchParams.get("type") || "all",
    patternType: searchParams.get("patternType") || "all",
    status: searchParams.get("status") || "all",
    category: searchParams.get("category") || "all",
    dateRange: searchParams.get("dateRange") || "all"
  });

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<RecurringTransaction | null>(null);

  // Map component filters to hook filters
  const hookFilters = useMemo(() => {
    const mappedFilters: any = {};

    // Map type
    if (filters.type !== "all") {
      mappedFilters.type = filters.type as "income" | "expense";
    }

    // Map pattern type
    if (filters.patternType !== "all") {
      mappedFilters.pattern = filters.patternType as "daily" | "weekly" | "monthly" | "yearly";
    }

    // Map status
    if (filters.status !== "all") {
      mappedFilters.status = filters.status as "active" | "paused" | "completed";
    }

    // Map category (would need category ID mapping)
    if (filters.category !== "all") {
      mappedFilters.category_id = parseInt(filters.category, 10);
    }

    return mappedFilters;
  }, [filters]);

  // Fetch recurring transactions using the real hook
  const {
    recurringTransactions: rawData,
    isLoading,
    error,
    pauseRecurring,
    resumeRecurring,
    deleteRecurring
  } = useRecurringTransactions(hookFilters);

  // Transform data to component format and apply search filter client-side
  const recurringTransactions = useMemo(() => {
    const transformed = transformRecurringTransactions(rawData);

    // Apply search filter client-side for instant feedback
    if (!debouncedSearchTerm) {
      return transformed;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    return transformed.filter((tx) =>
      tx.description.toLowerCase().includes(searchLower) ||
      tx.category?.toLowerCase().includes(searchLower) ||
      tx.notes?.toLowerCase().includes(searchLower)
    );
  }, [rawData, debouncedSearchTerm]);

  // Calculate statistics using transformed data
  const stats = useMemo(() => {
    return calculateRecurringStatistics(recurringTransactions);
  }, [recurringTransactions]);

  // Handle action handlers
  const handleEditTransaction = (transaction: RecurringTransaction) => {
    setEditingTransaction(transaction);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setEditingTransaction(null);
    toast.success("Transação recorrente atualizada com sucesso");
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditingTransaction(null);
  };

  const handlePauseTransaction = async (transactionId: string) => {
    try {
      await pauseRecurring.mutateAsync(transactionId);
      toast.success("Transação recorrente pausada com sucesso");
    } catch (error) {
      toast.error("Erro ao pausar transação recorrente");
      console.error("Error pausing recurring transaction:", error);
    }
  };

  const handleResumeTransaction = async (transactionId: string) => {
    try {
      await resumeRecurring.mutateAsync(transactionId);
      toast.success("Transação recorrente retomada com sucesso");
    } catch (error) {
      toast.error("Erro ao retomar transação recorrente");
      console.error("Error resuming recurring transaction:", error);
    }
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    try {
      await deleteRecurring.mutateAsync(transactionId);
      toast.success("Transação recorrente excluída com sucesso");
    } catch (error) {
      toast.error("Erro ao excluir transação recorrente");
      console.error("Error deleting recurring transaction:", error);
    }
  };

  const handleAddNewRecurring = () => {
    navigate("/transactions/new");
  };

  // Loading state
  if (isLoading && recurringTransactions.length === 0) {
    return (
      <MainLayout>
        <SEO
          title="Transações Recorrentes"
          description="Gerencie todas as suas transações recorrentes e automáticas"
          keywords="recorrente, automático, assinaturas, mensalidades"
        />
        <div className="flex items-center justify-center h-96">
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            <p className="text-muted-foreground">Carregando transações recorrentes...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error && recurringTransactions.length === 0) {
    return (
      <MainLayout>
        <SEO
          title="Transações Recorrentes"
          description="Gerencie todas as suas transações recorrentes e automáticas"
          keywords="recorrente, automático, assinaturas, mensalidades"
        />
        <div className="space-y-6">
          <RouteDisplay />

          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Ocorreu um erro ao carregar suas transações recorrentes. Por favor, tente novamente mais tarde.
            </AlertDescription>
          </Alert>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEO
        title="Transações Recorrentes"
        description="Gerencie todas as suas transações recorrentes e automáticas"
        keywords="recorrente, automático, assinaturas, mensalidades"
      />

      <div className="space-y-6">
        {/* Header Section with gradient text and pulsing indicator */}
        <section className="section-delimited">
          <RouteDisplay />
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Transações Recorrentes
              </h1>
              <p className="text-muted-foreground mt-1 flex items-center gap-2">
                <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
                {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </p>
            </div>
            <Button onClick={handleAddNewRecurring}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Recorrente
            </Button>
          </div>
        </section>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="card-elevated animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recorrências Ativas</CardTitle>
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeRecurringCount}</div>
              <p className="text-xs text-muted-foreground">
                Transações recorrentes ativas
              </p>
            </CardContent>
          </Card>

          <Card className="card-elevated animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Mensal</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.monthlyTotal.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Soma das recorrências mensais
              </p>
            </CardContent>
          </Card>

          <Card className="card-elevated animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Próxima Transação</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {stats.nextTransaction ? (
                <>
                  <div className="text-2xl font-bold">
                    {new Date(stats.nextTransaction.next_date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short'
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {stats.nextTransaction.description}
                  </p>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold">-</div>
                  <p className="text-xs text-muted-foreground">
                    Nenhuma transação próxima
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="card-elevated animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mais Frequentes</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.recentTransactions.length}
              </div>
              <p className="text-xs text-muted-foreground">
                Transações com ocorrências
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <section className="section-delimited pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-1 gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1 max-w-sm">
                <Input
                  placeholder="Buscar transações..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <RecurringFilters
                filters={filters}
                onFiltersChange={setFilters}
              />
            </div>
          </div>
        </section>

        {/* Transactions List */}
        <section className="section-delimited-darker p-0">
          <Card className="border-0 shadow-none">
            <CardHeader>
              <CardTitle>Transações Recorrentes</CardTitle>
              <CardDescription>
                {recurringTransactions.length} transações encontradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recurringTransactions.length === 0 ? (
                <Card className="card-elevated border-0">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Activity className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Nenhuma transação recorrente</h3>
                    <p className="text-muted-foreground mb-4 text-center max-w-sm">
                      Você ainda não possui transações recorrentes cadastradas.
                    </p>
                    <Button onClick={handleAddNewRecurring}>
                      <Plus className="mr-2 h-4 w-4" />
                      Criar Primeira Transação
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <ScrollArea className="h-[600px] pr-4">
                  <RecurringTransactionsList
                    transactions={recurringTransactions}
                    onEdit={handleEditTransaction}
                    onPause={handlePauseTransaction}
                    onResume={handleResumeTransaction}
                    onDelete={handleDeleteTransaction}
                    isLoading={isLoading}
                  />
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </section>
      </div>

      {/* Edit Modal */}
      <RecurringEditModal
        open={editModalOpen}
        onOpenChange={handleEditModalClose}
        transaction={editingTransaction}
        onSuccess={handleEditSuccess}
      />
    </MainLayout>
  );
}